// Annotation_Layer (enlarge-preview-annotate, design "AnnotationLayer.tsx"): HTML boxes and labels positioned over a
// rendered page. Text is rendered only as React text children, never as HTML (Req 10.6).
import { memo, useMemo, useState, type CSSProperties } from 'react';
import { isLow } from '../runs/ResultView';
import { layoutAnnotations, type PageSize } from './layout';
import { annotationAccessibleName, annotationLabel, visibleLabel, type Annotation } from './model';

/** Normal and low-confidence colors; both are about 4.9:1 against the white page (Req 6.11). */
export const ANNOTATION_COLOR = '#0972d3';
export const LOW_CONFIDENCE_COLOR = '#d91515';
/** Box fill alpha (12%, Req 6.10 allows at most 20%). */
export const FILL_ALPHA = 0.12;
export const OUTLINE_PX = 2;

const LABEL_FONT_FAMILY = '"Open Sans", "Helvetica Neue", Roboto, Arial, sans-serif';
const LABEL_PAD_X = 3;
const LABEL_BORDER = 1;
/** Fallback average glyph width as a fraction of the font size, used when canvas text metrics are unavailable. */
const FALLBACK_CHAR_EM = 0.6;

const lineHeightOf = (fontSize: number): number => Math.ceil(fontSize * 1.25);
const labelHeightOf = (fontSize: number): number => lineHeightOf(fontSize) + 2 * LABEL_BORDER;

const fillOf = (hex: string): string => {
  const n = Number.parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${FILL_ALPHA})`;
};

// One shared 2D context (or null when the environment has none, as in jsdom) and a per-font width cache.
let measureContext: CanvasRenderingContext2D | null | undefined;
const widthCache = new Map<string, number>();

function textWidth(text: string, fontSize: number): number {
  const font = `${fontSize}px ${LABEL_FONT_FAMILY}`;
  const cacheKey = `${font}\u0000${text}`;
  const cached = widthCache.get(cacheKey);
  if (cached !== undefined) return cached;
  if (measureContext === undefined) {
    try {
      measureContext = typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');
    } catch {
      measureContext = null;
    }
  }
  let width: number;
  if (measureContext) {
    measureContext.font = font;
    width = measureContext.measureText(text).width;
  } else {
    width = Array.from(text).length * fontSize * FALLBACK_CHAR_EM;
  }
  if (widthCache.size > 5000) widthCache.clear();
  widthCache.set(cacheKey, width);
  return width;
}

/** Rendered width of a label chip in CSS px: text, padding, and border, rounded up. */
export function measureLabel(text: string, fontSize: number): number {
  return Math.ceil(textWidth(text, fontSize) + 2 * (LABEL_PAD_X + LABEL_BORDER));
}

export interface AnnotationLayerProps {
  annotations: Annotation[];
  /** Size of the rendered page, in CSS px. */
  size: PageSize;
  showValues: boolean;
  withMember: boolean;
  threshold: number;
  /** True in the Enlarged_Viewer (Tab stops with accessible names); false in the Document_Viewer. */
  focusable: boolean;
  /** Req 6.3 minimums: 10 in the Document_Viewer, 12 in the Enlarged_Viewer at 100%. */
  fontSize: 10 | 12;
  /** Multiplier for the label font (the Enlarged_Viewer's zoom / 100). Defaults to 1. */
  scale?: number;
}

function AnnotationLayerImpl({
  annotations,
  size,
  showValues,
  withMember,
  threshold,
  focusable,
  fontSize,
  scale = 1,
}: AnnotationLayerProps) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const font = fontSize * scale;
  const labelHeight = labelHeightOf(font);

  const laidOut = useMemo(() => {
    const byKey = new Map(
      annotations.map((a) => {
        const full = annotationLabel(a, { showValues, withMember });
        return [a.key, { annotation: a, full, visible: visibleLabel(full) }] as const;
      }),
    );
    const layout = layoutAnnotations(
      annotations.map((a) => ({ key: a.key, location: a.location, text: byKey.get(a.key)!.visible })),
      size,
      { labelHeight, measure: (t) => measureLabel(t, font) },
    );
    return layout.map((l) => ({ ...l, ...byKey.get(l.key)! }));
  }, [annotations, size, showValues, withMember, labelHeight, font]);

  return (
    <div
      data-testid="annotation-layer"
      aria-hidden={focusable ? undefined : true}
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: size.width,
        height: size.height,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      {laidOut.map(({ key, box, label, annotation, full, visible }) => {
        const low = isLow(annotation.item.confidence, threshold);
        const color = low ? LOW_CONFIDENCE_COLOR : ANNOTATION_COLOR;
        const expanded = expandedKey === key;
        const text = expanded ? full : visible;
        // The expanded label may be wider than the visible one; keep it inside the page too.
        const labelX = expanded
          ? Math.min(Math.max(box.x, 0), Math.max(0, size.width - Math.min(measureLabel(full, font), size.width)))
          : label.x;
        const show = () => setExpandedKey(key);
        const hide = () => setExpandedKey((k) => (k === key ? null : k));
        const boxStyle: CSSProperties = {
          position: 'absolute',
          left: box.x,
          top: box.y,
          width: box.w,
          height: box.h,
          boxSizing: 'border-box',
          border: `${OUTLINE_PX}px ${low ? 'dashed' : 'solid'} ${color}`,
          background: fillOf(color),
          pointerEvents: 'auto',
          cursor: 'inherit',
        };
        const labelStyle: CSSProperties = {
          position: 'absolute',
          left: labelX,
          ...(label.placement === 'above' && expanded ? { bottom: size.height - box.y } : { top: label.y }),
          maxWidth: label.maxWidth,
          boxSizing: 'border-box',
          padding: `0 ${LABEL_PAD_X}px`,
          border: `${LABEL_BORDER}px solid ${color}`,
          borderRadius: 2,
          background: '#ffffff',
          color,
          fontFamily: LABEL_FONT_FAMILY,
          fontSize: font,
          lineHeight: `${lineHeightOf(font)}px`,
          fontWeight: 700,
          whiteSpace: expanded ? 'normal' : 'nowrap',
          overflowWrap: 'anywhere',
          overflow: 'hidden',
          pointerEvents: 'auto',
          zIndex: expanded ? 2 : 1,
        };
        return (
          <div key={key} data-annotation-key={key} data-low-confidence={low || undefined}>
            <div
              data-testid="annotation-box"
              style={boxStyle}
              {...(focusable
                ? {
                    tabIndex: 0,
                    role: 'img',
                    'aria-label': annotationAccessibleName(annotation, full, threshold),
                    onFocus: show,
                    onBlur: hide,
                  }
                : {})}
              onMouseEnter={show}
              onMouseLeave={hide}
            />
            <div
              data-testid="annotation-label"
              aria-hidden={focusable ? true : undefined}
              style={labelStyle}
              onMouseEnter={show}
              onMouseLeave={hide}
            >
              {text}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Boxes and labels for one page's Annotations; memoized so it re-renders only when its props change (Req 7.6). */
export const AnnotationLayer = memo(AnnotationLayerImpl);
